import { fabric } from 'fabric';

/**
 * CheckBackEngine - v41 Per-Design Backs
 *
 * Changes (2026-11-03, from the check-back research spec):
 * - The per-design config the callers already computed is now actually
 *   consumed: `tint` (field fill = pale mix of the design primary),
 *   `panelLines` (security panel wording sourced ONLY from the design's
 *   real features / template specs), `watermarkText`, variant extras
 *   (ImageMatch line, Treasury USA microprint endorsement line, eCheck
 *   instructions box, money-order / traveler's strip notes), frame
 *   treatments and the Canadian neutral footnote.
 * - The business deposit block prints the account-holder/business name
 *   above FOR DEPOSIT ONLY (the canonical stamp order).
 * - All new config fields are OPTIONAL: a caller that passes none of
 *   them (e.g. UltraRealisticCheckBuilder) gets exactly the previous
 *   behaviour — default field, canonical six panel lines, Reg CC
 *   footnote, ORIGINAL/DOCUMENT watermark.
 * - `signatureImage` and the unused depositConfig fields
 *   (name/date/signatureFont/handwritingColor) are accepted for
 *   backward compatibility and DELIBERATELY ignored: a check back must
 *   never carry a pre-printed payee signature, and nothing here may
 *   start rendering one.
 *
 * The endorsement-zone and security-box GEOMETRY follows the
 * owner's own hand-arranged reference back (his photo, re-measured
 * 2026-11-04): he found the previous arrangement "scattered" and
 * directed that HIS arrangement be the locked default for every
 * back — precise and print-ready, with users still free to drag
 * anything to their taste. The shared geometry constants live in
 * REFERENCE_BACK_GEOMETRY below; per-variant content (wording,
 * panel lines, frames, tiers) still comes from backVariants.ts.
 * Where the photo and the 2026-11-03 arrangement disagreed, the
 * photo won — each such change is listed at the constant.
 *
 * Panel grouping (2026-11-04): the security panel (and the eCheck
 * instructions box that stands in its place) is emitted as ONE
 * fabric Group — id `security-panel` / `echeck-instructions-panel`
 * — instead of a loose set of sibling objects. Dragging the frame
 * used to move only the rectangle and leave the feature list and
 * title behind, breaking the panel apart on the canvas; as a group
 * the whole slab selects, drags, deletes and restores as one unit
 * with one arrangement entry. The parts keep their established ids
 * INSIDE the group (their geometry is unchanged — the group is
 * constructed from the same absolute coordinates, so the rendered
 * layout at rest is identical), and the content is still rebuilt
 * from the caller's config on every render, so the panel's lines
 * stay dynamic. Callers that apply saved arrangements fall back to
 * the legacy frame entry (`security-box-frame` /
 * `echeck-instructions-frame`) when no group entry exists yet, so
 * arrangements saved before the grouping still land on the panel.
 */
/**
 * The reference back's shared geometry, measured from the owner's
 * photo (850x350 canvas units; the engine's scale multiplies them).
 * Photo fractions were converted per axis (x as % of the back's
 * width, y as % of its height); grounding error is roughly ±5 units.
 *
 * Changes from the 2026-11-03 arrangement (photo wins, per owner):
 * - Endorsement strip is 210 wide, not 108: in the photo the strip
 *   includes the financial-institution area left of the endorsement
 *   lines (divider at 75.3% of the width, not 87.3%).
 * - Security box: left 30 (was 2 — the photo's box is inset, not
 *   edge-hugging), width 148 (was 200), top 26, height H-52.
 * - Feature pitch 17 (was 25), first line at box offset 25, title
 *   column at offset 138 (was 174), footnote at offset 8 (was 10).
 * - Endorsement lines: CORRECTED 2026-11-04 (second pass — the
 *   arrangement pass above mis-measured the photo: it took the X's
 *   neighbourhood for the first line and halved the lines' run).
 *   Pixel-measured from the photo (line-grey isolation, back
 *   812x357 px, divider at x 643, strip 169 px wide): the three
 *   lines sit at strip fractions 0.296 / 0.494 / 0.695 (offsets
 *   50 / 84 / 117 photo px), pitch 33.5 photo px (0.198 of the
 *   strip), tops aligned at 0.176 of height, bottoms aligned at
 *   0.905 of height — long, evenly spaced rails, not bunched at
 *   the trailing edge. In canvas units (strip 210): first offset
 *   62, pitch 42, tops y 62, bottoms at 0.90 of height. The X
 *   stands at the head of the LAST line (the one nearest the
 *   ENDORSE HERE column), not the first.
 * - The 2026-11-03 "lower band" (columns centred near y 271) is
 *   abolished: in the photo every strip column centres on the
 *   strip's vertical middle (~49% of height), and the mobile
 *   checkbox sits at the HEAD of its column (y ~104), not in a
 *   bottom band. That stagger was the "scattered" look.
 * - Warning/reserved are adjacent columns at the strip's left edge
 *   (offsets 28/19), not split across the strip (was 0.5 / 0.2).
 *   SUPERSEDED for the reserved column on 2026-11-04 by the
 *   owner's close-up of the same back's endorsement end
 *   (image-34.png): the reserved line stands in the field at the
 *   strip boundary, in the specimen's +90 direction — see
 *   reservedFieldOffset.
 * - THE SPECIMEN PATTERN (owner's FINAL ruling, 2026-11-04, from
 *   his reference back in image-33.png — it supersedes both the
 *   same-day "centre the write-up horizontally" composition and
 *   the "no rotated panels" rule): every back's security panel
 *   write-up is ROTATED +90 degrees, in the specimen's direction
 *   (letter-tops toward the middle of the check) along the tall
 *   frame — title at the frame's right, feature lines in order
 *   toward the left, the wash box last in the stack — and the
 *   Reg CC footnote runs vertically along the far-left edge. "Any
 *   design you are building on the different back of check must
 *   follow that pattern." The composition lives in
 *   addUnlockedSecurityBox (and the eCheck instructions box in its
 *   place); the endorsement strip keeps its own arrangement,
 *   audited against image-34.png the same day.
 * - Mobile/deposit columns at strip offsets 48/63 (was 0.75/0.85
 *   of 108) — same columns, re-based on the wider strip.
 */
export const REFERENCE_BACK_GEOMETRY = {
    stripWidth: 210,
    stripCenterYRatio: 0.49,
    endorseLineFirstOffset: 62,
    endorseLinePitch: 42,
    endorseLineStartTop: 62,
    endorseLineEndRatio: 0.9,
    headerLeftInset: 14,
    headerTop: 76,
    // The RESERVED column no longer stands inside the strip: on the
    // owner's reference back (image-34.png, 2026-11-04) it sits in
    // the FIELD at the boundary with the strip, reading UPWARD —
    // the opposite direction to the deposit columns. Its centre
    // stands this far left of the divider.
    reservedFieldOffset: 10,
    warningOffset: 28,
    mobileTextOffset: 48,
    mobileBoxOffset: 63,
    mobileTextCenterYRatio: 0.52,
    mobileBoxCenterY: 104,
    // Panel anchor: the LEFT placement of the owner's reference
    // back (boxLeft 30), as it has been since the re-alignment.
    // The compliance footnote is NOT part of the panel group: it
    // stands on the far-left edge of the back (see regccColumnX
    // below), outside the frame — owner rulings, 2026-11-04. The
    // geometry tests pin boxLeft 30, the rotated write-up's
    // composition inside the frame, and the footnote's edge seat.
    boxLeft: 30,
    boxTop: 26,
    boxWidth: 148,
    boxBottomMargin: 52,
    // The Reg CC footnote column: a vertical line centred this far
    // from the back's left edge, in the specimen's direction
    // alongside the panel — the seat it holds on the owner's
    // reference back
    // (image-33.png: the line hugs the sheet's left edge, clear of
    // the panel frame at boxLeft 30).
    regccColumnX: 10
};

/**
 * The four corner controls from fabric's default control set —
 * and nothing else. Used for the mobile-deposit block: on a tall,
 * narrow object the edge-midpoint controls (mt/mb/ml/mr) and the
 * rotate handle (mtr) land on the object's own glyphs, and fabric
 * checks the active object's controls before it drags, so a grab
 * on the glyphs is stolen into a one-axis scale/rotate instead of
 * moving the object (see the block's construction site). Corner
 * scaling is proportional, which is the only resize this block
 * should ever take.
 */
export function cornerOnlyControls(): Record<string, any> {
    const defaults = ((fabric as any).Object?.prototype?.controls || {}) as Record<string, any>;
    const out: Record<string, any> = {};
    for (const key of ['tl', 'tr', 'bl', 'br']) {
        if (defaults[key]) out[key] = defaults[key];
    }
    return out;
}

export class CheckBackEngine {
    static generateCheckBack(width: number, height: number, config: any, scaleFactor: number, depositConfig?: any, signatureImage?: string | null): fabric.Object[] {
        // Deliberately unused — see the header comment. Backs never
        // render a signature.
        void signatureImage;
        const objects: fabric.Object[] = [];
        const scale = scaleFactor / 72;
        const cfg = config || {};
        const style = cfg.style || 'mobile'; // Default to mobile if not specified

        // 0. Base Draggable Backgrounds (User can adjust split manually)
        const endorsementX = width - (REFERENCE_BACK_GEOMETRY.stripWidth * scale);

        // Left Panel (Main Security Body) — the design's own field: a
        // pale mix of its primary colour (a *screen*, kept light enough
        // for bank stamps to stay legible), or the variant's explicit
        // field fill. No tint -> the previous default for every caller
        // that does not supply one.
        const fieldFill = cfg.fieldFill || (cfg.tint ? paleMix(cfg.tint) : '#e3f2fd');
        objects.push(new fabric.Rect({
            id: 'back-bg-left', // UNIQUE ID
            left: 0,
            top: 0,
            width: endorsementX,
            height: height,
            fill: fieldFill,
            selectable: true, evented: true, hasControls: true, lockRotation: true
        } as any));

        // Right Panel (Endorsement Zone - the warm ivory of the reference
        // back; Treasury gets a pale green ivory, the printed eCheck
        // plain white — both arrive as cfg.stripFill)
        objects.push(new fabric.Rect({
            id: 'back-bg-right', // UNIQUE ID
            left: endorsementX,
            top: 0,
            width: width - endorsementX,
            height: height,
            fill: cfg.stripFill || '#faf6ec',
            selectable: true, evented: true, hasControls: true, lockRotation: true
        } as any));

        // 1. Watermark (Horizontal Center - Draggable, Big & Bold).
        //    watermarkText === null means the variant prints none (V7/V7L).
        if (cfg.watermarkText !== null) {
            this.addHorizontalWatermark(width, height, scale, objects, cfg.watermarkText || ['ORIGINAL', 'DOCUMENT']);
        }

        // 1b. ImageMatch line (V3 bank-issued): matching numbers from
        //     the front repeated in light ink in the open FI field, so
        //     front/back images can be matched. Default content is the
        //     routing + check numbers; a variant whose real feature
        //     prints account + check (USAA, payload content
        //     'account-check') carries the account number instead.
        //     Each segment prints only when its number exists, and
        //     nothing renders when both are blank. Open field only —
        //     never in the strip, never in the panel.
        if (cfg.imageMatch) {
            const im = cfg.imageMatch;
            const firstNumber = im.content === 'account-check' ? (im.accountNumber || '') : (im.routingNumber || '');
            const lineText = [firstNumber, im.checkNumber || ''].filter(Boolean).join('  ');
            if (lineText) {
                const openL = (REFERENCE_BACK_GEOMETRY.boxLeft + REFERENCE_BACK_GEOMETRY.boxWidth) * scale;
                objects.push(new fabric.Text(lineText, {
                    id: 'back-imagematch', // UNIQUE ID
                    left: openL + (36 * scale),
                    top: height * 0.5,
                    fontSize: 9 * scale,
                    fontFamily: 'Courier New, monospace',
                    fill: 'rgba(0, 0, 0, 0.28)',
                    angle: 90,
                    originX: 'center',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            }
        }

        // 2. Endorsement Zone (Right Side - All elements unlocked)
        this.addUnlockedEndorsementZone(width, height, scale, objects, style, depositConfig, cfg);

        // 3. Security Box (reference slab - All elements unlocked), or
        //    the variant that replaces it. panelMode 'none' prints NO
        //    security panel (a printed eCheck on plain paper has no
        //    security features to claim); V7 stands an instructions
        //    box in the panel's place instead.
        if (cfg.panelMode === 'none') {
            if (cfg.instructions) {
                this.addEcheckInstructionsBox(width, height, scale, objects);
            }
        } else {
            this.addUnlockedSecurityBox(width, height, scale, objects, {
                lines: cfg.panelLines,
                extended: !!cfg.panelExtended,
                footnote: cfg.footnote || 'regcc',
                frameStroke: cfg.frameStroke || null,
                frameInner: cfg.frameInner || null,
                washStyle: cfg.washBoxStyle || null
            });
            // The Chemical Wash Detection Box lives INSIDE the
            // panel, beneath the feature list, in its per-design
            // style — composed by addUnlockedSecurityBox itself
            // (Princewill, 2026-11-04: "put back the chemical box
            // where it is, but make sure they have different
            // styles"). It renders only when cfg.washBoxStyle is
            // set, i.e. only on chemical-capable designs.
        }

        return objects;
    }

    private static addHorizontalWatermark(width: number, height: number, scale: number, objects: fabric.Object[], lines: [string, string]): void {
        const endorsementX = width - (REFERENCE_BACK_GEOMETRY.stripWidth * scale);
        // The open area between the security box (ends at boxLeft +
        // boxWidth, see addUnlockedSecurityBox) and the endorsement
        // strip. The watermark is centred and sized on THIS area so
        // it never runs under the box or into the strip.
        const openL = (REFERENCE_BACK_GEOMETRY.boxLeft + REFERENCE_BACK_GEOMETRY.boxWidth) * scale;
        const openW = endorsementX - openL;
        // The fit targets ~92% of the open area for the longest
        // word, using Arial Black's measured average cap advance
        // (~0.775em per character — the 8 capitals of DOCUMENT sum
        // to ~6.19em). Owner, 2026-11-04: "That original needs to
        // be at least a little bit bigger. The letter is a bit
        // tiny." The previous fit ((openW - 24) / (longest *
        // 0.9675)) set DOCUMENT at ~56.6 on the 850-wide back;
        // this fit sets it at ~68.7 (+21%) — a step up, still a
        // light screen (the fill is unchanged), and because the
        // text stays centred on the open area with its span
        // targeted inside it, the letters can never reach the
        // panel frame on the left or the endorsement strip on the
        // right. The 96 cap is unchanged.
        const longest = Math.max(lines[0].length, lines[1].length, 1);
        const commonProps = {
            left: openL + (openW / 2),
            fontSize: Math.min(96 * scale, ((openW * 0.92) / (longest * 0.775))),
            fontFamily: 'Arial Black, Arial',
            fontWeight: 900,
            fill: 'rgba(180, 180, 180, 0.18)',
            originX: 'center',
            originY: 'center',
            selectable: true, evented: true, hasControls: true,
            charSpacing: 16 * scale
        };

        objects.push(new fabric.Text(lines[0], {
            ...commonProps,
            id: 'watermark-original', // UNIQUE ID
            top: (height * 0.38)
        } as any));

        objects.push(new fabric.Text(lines[1], {
            ...commonProps,
            id: 'watermark-document', // UNIQUE ID
            top: (height * 0.62)
        } as any));
    }

    private static addUnlockedEndorsementZone(width: number, height: number, scale: number, objects: fabric.Object[], style: string, config?: any, backCfg?: any): void {
        const G = REFERENCE_BACK_GEOMETRY;
        const zoneWidth = G.stripWidth * scale;
        const zoneX = width - zoneWidth;

        // THE OWNER'S REFERENCE ARRANGEMENT (re-measured 2026-11-04
        // from his own hand-arranged back — see REFERENCE_BACK_GEOMETRY
        // for the deltas against the previous arrangement). The strip
        // includes the financial-institution area: divider, then the
        // reserved/warning columns at the strip's left edge, the
        // mobile/deposit columns, and the three endorsement lines in
        // the right portion with the X on the last line's head and
        // ENDORSE HERE at the edge. Every notice column centres on
        // one shared line — the strip's vertical middle — so the
        // strip reads as ordered blocks, never staggered.

        // --- Divider Line ---
        objects.push(new fabric.Line([zoneX, 0, zoneX, height], {
            id: 'endorse-divider', // UNIQUE ID
            stroke: '#999', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- "ENDORSE HERE" Header ---
        objects.push(new fabric.Text("ENDORSE HERE", {
            id: 'endorse-header', // UNIQUE ID
            left: width - (G.headerLeftInset * scale),
            top: G.headerTop * scale,
            fontSize: 12 * scale,
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fill: '#333',
            angle: 90,
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- Signature X ---
        // On the reference back the X stands at the head of the LAST
        // endorsement line — the line nearest the ENDORSE HERE
        // column — on that line's own column, not beside it. (The
        // arrangement pass originally put it on the first line; the
        // pixel re-measurement corrected that — see the geometry
        // comment at REFERENCE_BACK_GEOMETRY.)
        const lineColumnX = (i: number) => zoneX + ((G.endorseLineFirstOffset + i * G.endorseLinePitch) * scale);
        const xPosX = lineColumnX(2);
        objects.push(new fabric.Text("X", {
            id: 'endorse-x', // UNIQUE ID
            left: xPosX,
            top: G.endorseLineStartTop * scale,
            fontSize: 16 * scale,
            fontFamily: 'Arial',
            fill: '#000',
            angle: 90,
            originX: 'center',
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- 3 Endorsement Lines (the reference spacing: three
        // evenly pitched rails, tops and bottoms aligned, running
        // 0.176 -> 0.90 of the height) ---
        const lineStartTop = G.endorseLineStartTop * scale;
        const lineEndTop = height * G.endorseLineEndRatio;

        for (let i = 0; i < 3; i++) {
            const lx = lineColumnX(i);
            if (i === 0 && backCfg?.treasuryMicroprint) {
                // U.S. Treasury treatment: the first endorsement line is
                // microprinted "USAUSAUSA…" — SAME coordinates, SAME id,
                // only the rendering differs (a repeating microtext runs
                // along the line instead of a stroke).
                objects.push(new fabric.Text('USA'.repeat(16), {
                    id: 'endorse-line-0', // UNIQUE ID — unchanged
                    left: lx,
                    top: lineStartTop,
                    fontSize: 4.5 * scale,
                    fontFamily: 'Arial',
                    fill: '#555',
                    angle: 90,
                    originX: 'left',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
                continue;
            }
            objects.push(new fabric.Line([lx, lineStartTop, lx, lineEndTop], {
                id: `endorse-line-${i}`, // UNIQUE ID
                stroke: '#ccc', strokeWidth: 1.5 * scale,
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // --- Shared centre line ---
        // In the reference every notice column in the strip centres
        // on the strip's vertical middle (49% of the height) — one
        // shared line, no stagger. (The 2026-11-03 "lower band" that
        // centred them near y 271 is what read as scattered to the
        // owner; his photo supersedes it.)
        const stripCenterY = height * G.stripCenterYRatio;

        // --- Mobile Deposit Area ---
        // Reference arrangement: the checkbox sits at the HEAD of the
        // mobile column (near the top, y ~104), and the instruction
        // text runs down the same column centred a little below the
        // strip's middle.
        //
        // The block is ONE fabric Group (id 'mobile-deposit-block')
        // for the same reason as the security panel: the tick box and
        // its label are one logical unit and must select, drag, scale,
        // delete and restore together. As siblings they did not —
        // dragging or enlarging the box left the label behind at its
        // original seat and size (owner report, 2026-11-04: he enlarges
        // the block BECAUSE the label text is too small, so a box that
        // grows while its label stays tiny defeats the purpose). The
        // parts are built at the same absolute coordinates as always
        // and keep their ids inside the group; the group is anchored
        // at the union of the parts' bounding rects (the box's
        // top-left — the box heads the column and is wider than the
        // rotated label), so every part renders exactly where it did
        // as a sibling object. The arrangement system records a single
        // transform for the whole block under 'mobile-deposit-block';
        // arrangements saved while the pieces were siblings are
        // healed at lookup (see backPositionFor in CheckBuilder /
        // mobileDepositBlockEntryFromLegacy in utils/backDefault).
        if (style === 'mobile') {
            const parts: fabric.Object[] = [];

            parts.push(new fabric.Text("CHECK HERE IF MOBILE DEPOSIT", {
                id: 'mobile-instruction', // UNIQUE ID
                left: zoneX + (G.mobileTextOffset * scale),
                top: height * G.mobileTextCenterYRatio,
                fontSize: 7 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#222',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));

            parts.push(new fabric.Rect({
                id: 'mobile-checkbox', // UNIQUE ID
                // The box heads the MOBILE COLUMN (mobileTextOffset),
                // not the deposit-account column: at mobileBoxOffset
                // (63) its 16-wide body was centred on x 703 and the
                // first endorsement line (offset 62, x 702) ran
                // straight through it (owner fix, 2026-11-04). On
                // the mobile column it spans offsets 40-56 - clear of
                // the warning column (28) and 6 units clear of the
                // first endorsement line.
                left: zoneX + (G.mobileTextOffset * scale),
                top: G.mobileBoxCenterY * scale,
                width: 16 * scale,
                height: 16 * scale,
                fill: 'transparent',
                stroke: '#222',
                strokeWidth: 1.5 * scale,
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));

            // Emit the block as ONE unit, anchored at the union of
            // the parts' bounding rects — fabric's own group anchor
            // for these parts (probe-verified against real fabric
            // 5.5.2: a group constructed from parts at absolute seats
            // takes the union top-left as its left/top, and passing
            // that same anchor explicitly renders every part exactly
            // where it stood as a sibling).
            let anchorLeft = Infinity;
            let anchorTop = Infinity;
            parts.forEach(part => {
                (part as any).setCoords();
                const b = (part as any).getBoundingRect(true, true);
                if (b.left < anchorLeft) anchorLeft = b.left;
                if (b.top < anchorTop) anchorTop = b.top;
            });
            const block = new fabric.Group(parts, {
                id: 'mobile-deposit-block', // UNIQUE ID — the whole block is one draggable unit
                left: anchorLeft, top: anchorTop,
                selectable: true, evented: true, hasControls: true
            } as any);
            // CORNER CONTROLS ONLY (parent live re-check, 2026-11-04:
            // the grouped block still "split" under a real grab).
            // The block is a tall, narrow column (17.5 wide) whose
            // OWN GLYPHS sit on the group's edge-midpoint control
            // seats: the tick box heads the column at top-centre
            // (the 'mt' seat) and the rotated label's centre/tail
            // stand on the bottom-centre (the 'mb' seat — probe-
            // verified against real fabric 5.5.2: _findTargetCorner
            // at the label's centre returns 'mb'). fabric checks the
            // ACTIVE object's controls before it drags, so a press
            // meant to drag the already-selected block BY ITS LABEL
            // was stolen by 'mb' and became a one-axis scaleY —
            // left/top frozen, the label stretched away alone and
            // the box stranded at a sub-visible size ("the label
            // moved alone; the checkbox is gone"). The touch-drag
            // precedence (utils/touchDrag) rewrites exactly this
            // theft for TOUCH presses on tiny targets; a mouse press
            // had no such cover. With only the four corner controls
            // left, every grab on the block's glyphs is a plain drag
            // of the whole block, and enlarging happens from a
            // corner — growing box and label together, proportionally
            // (the enlargement the owner actually wants). The rotate
            // control goes too: the block never rotates — its pieces
            // carry the specimen +90 inside the group.
            (block as any).controls = cornerOnlyControls();
            objects.push(block);
        }

        // --- Deposit-Only Styles (Business / ATM) ---
        // Same columns as the mobile block above, and the same shared
        // centre line: the deposit style changes the restrictive
        // endorsement wording, never the owner's layout. The business
        // block follows the canonical endorsement-stamp order —
        // account-holder/business NAME, then FOR DEPOSIT ONLY, then
        // the account number — with the name stacked above the
        // restriction in the same column when a name is configured.
        if (style === 'business' || style === 'atm') {
            const businessName = style === 'business' ? (config?.businessName ? String(config.businessName) : '') : '';
            if (businessName) {
                objects.push(new fabric.Text(businessName.toUpperCase(), {
                    id: 'deposit-only-name',
                    left: zoneX + (G.mobileTextOffset * scale),
                    top: stripCenterY - (34 * scale),
                    fontSize: 6 * scale,
                    fontWeight: 'bold',
                    fontFamily: 'Arial',
                    fill: '#222',
                    angle: 90,
                    originX: 'center',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            }
            const depositY = businessName ? stripCenterY + (24 * scale) : stripCenterY;
            objects.push(new fabric.Text("FOR DEPOSIT ONLY", {
                id: 'deposit-only-label',
                left: zoneX + (G.mobileTextOffset * scale),
                top: depositY,
                fontSize: 7 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#222',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
            if (style === 'business' && config?.accountNumber) {
                objects.push(new fabric.Text(String(config.accountNumber), {
                    id: 'deposit-only-account',
                    left: zoneX + (G.mobileBoxOffset * scale),
                    top: depositY,
                    fontSize: 7 * scale,
                    fontFamily: 'Arial',
                    fill: '#222',
                    angle: 90,
                    originX: 'center',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            }
        }

        // --- Warning Text ---
        // In the reference the warning runs in a column immediately
        // beside the reserved column at the strip's left edge — the
        // two read as one financial-institution block. At 5 units the
        // text is ~125 long, so centred on the shared centre line it
        // fits the strip's height with margin at both ends. Darkened
        // to #333 so it reads as the zone's boundary legend.
        objects.push(new fabric.Text("DO NOT WRITE, SIGN OR STAMP BELOW THIS LINE", {
            id: 'endorse-warning', // UNIQUE ID
            left: zoneX + (G.warningOffset * scale),
            top: stripCenterY,
            fontSize: 5 * scale,
            fontWeight: '800',
            fontFamily: 'Arial',
            fill: '#333',
            angle: 90,
            originX: 'center',
            originY: 'center',
            selectable: true, evented: true, hasControls: true
        } as any));

        // --- Reserved Text ---
        // The owner's close-up of the reference back's endorsement
        // end (image-34.png, 2026-11-04) settles this column's
        // SEAT: the RESERVED line does NOT stand inside the strip
        // with the deposit columns. It stands in the FIELD at the
        // boundary between the tinted back and the cream strip,
        // just left of the divider. Its DIRECTION was settled by
        // the same photo under a crop-and-rotate test (2026-11-04):
        // rotating the RESERVED + DO NOT WRITE region 90 degrees
        // clockwise left BOTH texts upside down, so both are +90
        // in the specimen — the same family as the deposit columns
        // and ENDORSE HERE, letter-tops toward the strip. (An
        // earlier reading had it at -90, "opposite to the deposit
        // columns"; the rotation test disproved that.) A variant
        // may replace this column with its own strip note
        // (money order: PAYEE ENDORSEMENT ONLY; traveler's check:
        // COUNTERSIGNATURE ON FRONT) — the note takes the same
        // boundary seat and the same +90 direction, and the two
        // texts never crowd one column.
        if (backCfg?.stripNote) {
            objects.push(new fabric.Text(String(backCfg.stripNote), {
                id: 'strip-note', // UNIQUE ID
                left: zoneX - (G.reservedFieldOffset * scale),
                top: stripCenterY,
                fontSize: 5.5 * scale,
                fontWeight: 'bold',
                fontFamily: 'Arial',
                fill: '#555',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        } else {
            objects.push(new fabric.Text("RESERVED FOR FINANCIAL INSTITUTION USE*", {
                id: 'endorse-reserved', // UNIQUE ID
                left: zoneX - (G.reservedFieldOffset * scale),
                top: stripCenterY,
                fontSize: 5 * scale,
                fontFamily: 'Arial',
                fill: '#888',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        }
    }

    private static addEcheckInstructionsBox(width: number, height: number, scale: number, objects: fabric.Object[]): void {
        // V7 Printed eCheck: the recipient prints the front on plain
        // paper — there IS no security stock and no feature list to
        // print. An instructions box stands in the security panel's
        // place (same slab geometry) and says exactly that, mirroring
        // the issuer guidance for printed eChecks.
        const G = REFERENCE_BACK_GEOMETRY;
        const boxLeft = G.boxLeft * scale;
        const boxTop = G.boxTop * scale;
        const boxW = G.boxWidth * scale;
        const boxH = height - (G.boxBottomMargin * scale);

        // The box is ONE fabric Group (id 'echeck-instructions-panel')
        // for the same reason as the security panel it replaces:
        // frame and texts are one logical slab and must select, drag,
        // delete and restore together. Parts keep their ids inside
        // the group, built at the same absolute coordinates.
        const parts: fabric.Object[] = [];

        parts.push(new fabric.Rect({
            id: 'echeck-instructions-frame', // UNIQUE ID
            left: boxLeft, top: boxTop, width: boxW, height: boxH,
            fill: 'transparent', stroke: '#888', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));

        // ORIENTATION (fidelity orientation audit 2026-11-04; defect
        // re-verified by render at the MAJOR close-out 2026-11-05):
        // the instructions write-up reads HORIZONTALLY — stacked
        // rows centred in the frame. This box is not the specimen
        // security panel: it is a printed eCheck's instruction slab
        // (the recipient prints the sheet and reads the instructions
        // as printed), and its lines previously ran rotated up the
        // slab, which the audit ruled a defect. Ids and the group
        // are unchanged; only the composition rotated back.
        const colY = boxTop + (boxH / 2);
        const rowText = (id: string, text: string, rowOffset: number, fontSize: number, fontWeight: string) => {
            parts.push(new fabric.Text(text, {
                id,
                left: boxLeft + (boxW / 2),
                top: colY + (rowOffset * scale),
                fontSize: fontSize * scale,
                fontWeight,
                fontFamily: 'Arial',
                fill: '#333',
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        };

        rowText('echeck-instructions', 'PRINTED ELECTRONIC CHECK', -16, 8, 'bold');
        rowText('echeck-instructions-2', 'ENDORSE IN THE AREA ABOVE', 0, 7, 'normal');
        rowText('echeck-instructions-3', 'NO SECURITY PAPER FEATURES APPLY', 16, 7, 'normal');

        objects.push(new fabric.Group(parts, {
            id: 'echeck-instructions-panel', // UNIQUE ID — the whole box is one draggable unit
            left: boxLeft, top: boxTop,
            selectable: true, evented: true, hasControls: true
        } as any));
    }

    private static addUnlockedSecurityBox(width: number, height: number, scale: number, objects: fabric.Object[], opts?: { lines?: string[]; extended?: boolean; footnote?: string; frameStroke?: string | null; frameInner?: string | null; washStyle?: string | null }): void {
        // The security box stands where the owner's reference back
        // puts it (left placement, boxLeft 30 — see
        // REFERENCE_BACK_GEOMETRY), a tall slab with ~7% margins
        // top and bottom. Inside, the write-up follows THE SPECIMEN
        // PATTERN (Princewill's FINAL back ruling, 2026-11-04, from
        // his reference back in image-33.png — it supersedes both
        // the horizontal centred block composed earlier that day
        // and the "no rotated panels" rule): the SECURITY FEATURES
        // title and the feature lines are ROTATED +90 degrees —
        // the specimen's direction, letter-tops toward the middle
        // of the check (the ENDORSE HERE family; an earlier pass
        // used -90, which faces the write-up toward the left edge
        // — Princewill's "facing down" — and he ordered it faced
        // up, 2026-11-04) — the title at the
        // frame's right, the features in order toward the left, and
        // — ONLY on designs whose paper is chemical-sensitive — the
        // Chemical Wash Detection Box closing the stack at the
        // frame's left, drawn in the design family's own wash style
        // (opts.washStyle; Princewill, 2026-11-04: the box sits
        // inside the panel, "but make sure they have different
        // styles"). On any other design no wash box renders,
        // in-panel or anywhere. The small-print compliance footnote
        // does NOT live in the panel group: it runs vertically
        // along the back's far-left edge, in the specimen's own
        // direction like the panel write-up (see the emission below).
        const G = REFERENCE_BACK_GEOMETRY;
        const boxLeft = G.boxLeft * scale;
        const boxTop = G.boxTop * scale;
        const boxW = G.boxWidth * scale;
        const boxH = height - (G.boxBottomMargin * scale);

        // The panel's parts are collected and emitted as ONE fabric
        // Group (id 'security-panel') at the end of this function:
        // frame, feature lines, title and the wash box are one
        // printed slab and must select, drag, delete and restore as
        // one unit. The parts are built at the same absolute
        // coordinates as always and keep their ids inside the group,
        // so the content stays fully dynamic (rebuilt from opts on
        // every call). The compliance footnote is the one panel
        // furnishing NOT in the group — it is emitted separately,
        // below — so the group's bounds remain exactly the frame's.
        const parts: fabric.Object[] = [];

        // Frame — the stroke follows the design family (V8/V9 navy,
        // V10 the design primary, canonical grey otherwise), and a
        // variant may add a thin inner rule inside the same frame
        // (BofA two-tone, V3 double rule). The frame geometry itself
        // never changes.
        parts.push(new fabric.Rect({
            id: 'security-box-frame', // UNIQUE ID
            left: boxLeft, top: boxTop, width: boxW, height: boxH,
            fill: 'transparent', stroke: opts?.frameStroke || '#888', strokeWidth: 1.2 * scale,
            selectable: true, evented: true, hasControls: true
        } as any));
        if (opts?.frameInner) {
            parts.push(new fabric.Rect({
                id: 'security-box-inner-frame', // UNIQUE ID
                left: boxLeft + (4 * scale), top: boxTop + (4 * scale),
                width: boxW - (8 * scale), height: boxH - (8 * scale),
                fill: 'transparent', stroke: opts.frameInner, strokeWidth: 0.8 * scale,
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // The feature LINES come from the caller (config panelLines —
        // sourced only from the design's real features or its
        // template's own specs). No lines supplied -> the canonical
        // six, the backward-compatible behaviour for callers that
        // predate per-design backs. An explicitly EMPTY list prints
        // an empty panel: a design must not claim features its front
        // does not have.
        const features = opts?.lines ?? [
            "• FLUORESCENT FIBER TECHNOLOGY",
            "• CHEMICAL SENSITIVE PAPER STOCK",
            "• MICROPRINTING ALONG LINES (MP)",
            "• SECURITY SCREEN BACKDROP",
            "• ROUTING NUMBER VERIFICATION",
            "• AUTHENTIC DOCUMENT WATERMARK"
        ];

        // The footnote small print. US backs carry the Reg CC line;
        // the Canadian variant swaps in a neutral line (CPA practice
        // — no Reg CC claim), and a footnote of 'none' prints none.
        const footnote = opts?.footnote ?? 'regcc';
        const footnoteText = footnote === 'regcc'
            ? "FEDERAL RESERVE REG CC COMPLIANT"
            : footnote === 'neutral'
                ? "SECURITY FEATURES — SEE FRONT"
                : null;

        // Compose the write-up in a VIRTUAL READING SPACE: a
        // horizontal warning box whose width is the frame's HEIGHT
        // (the reading direction, u) and whose height is the frame's
        // WIDTH (the stacking direction, v). The stack order is the
        // one the panel has always used — title, a gap, the feature
        // lines on an even pitch, then (chemical designs only) the
        // wash label and box — and the stack as a whole is centred
        // on the v axis. Every part is then mapped into the tall
        // frame by the rigid transform
        //     virtual (u, v) -> canvas (boxLeft + boxW - v, boxTop + u)
        // with texts taking angle +90, which is exactly the
        // reference back's arrangement: title at the frame's right,
        // features in order toward the left, wash stack last, every
        // line in the specimen's direction (letter-tops toward the
        // middle of the check) and centred on the frame's midline.
        const VW = boxH;
        const VH = boxW;
        const centreU = VW / 2;
        const titleFont = 10 * scale;
        const longest = features.reduce((m, l) => Math.max(m, l.length), 0);
        const featureFont = Math.min(7 * scale, longest > 0 ? (VW - (20 * scale)) / (longest * 0.60) : 7 * scale);
        let pitch = (features.length > 6 ? 9.5 : 11) * scale;
        const titleH = 12 * scale;
        const gapH = 6 * scale;
        let blockH = titleH + gapH + (features.length * pitch);
        const zoneTop = 8 * scale;
        const zoneBottom = VH - (8 * scale);
        const zoneH = zoneBottom - zoneTop;
        // The wash zone (chemical-capable designs only): a label
        // line and the blank detection field after the feature list
        // in the stack, inside the frame. The rotated frame is much
        // shorter along the stacking axis than the horizontal
        // composition's frame was (148 units, not 298), so the wash
        // field's old 36-unit floor gives way: it takes what the
        // stack leaves (24 at least), and if even that cannot fit,
        // the feature pitch tightens until it can — the box never
        // spills past the frame's border. A design without the box
        // keeps the centred write-up block alone (stackH == blockH
        // when washStyle is null).
        const washStyle = opts?.washStyle || null;
        const washLabelH = washStyle && washStyle !== 'header' ? (9 * scale) : 0;
        const washGap = washStyle ? (8 * scale) : 0;
        let washH = 0;
        if (washStyle) {
            washH = Math.max(24 * scale, Math.min(76 * scale, zoneH - blockH - washGap - washLabelH));
            if (blockH + washGap + washLabelH + washH > zoneH && features.length > 0) {
                const excess = (blockH + washGap + washLabelH + washH) - zoneH;
                pitch = Math.max(8 * scale, pitch - (excess / features.length));
                blockH = titleH + gapH + (features.length * pitch);
                washH = Math.max(20 * scale, Math.min(76 * scale, zoneH - blockH - washGap - washLabelH));
            }
        }
        const stackH = blockH + washGap + washLabelH + washH;
        const blockTop = zoneTop + Math.max(0, (zoneH - stackH) / 2);
        const featuresTop = blockTop + titleH + gapH;
        const washLabelTop = blockTop + blockH + washGap;
        const washBoxTop = washLabelTop + washLabelH;
        const washW = VW - (20 * scale);
        const washLeft = (VW - washW) / 2;

        // A write-up line whose stack centre is v: rotated +90
        // (the specimen's direction, letter-tops toward the
        // middle of the check), centred on the frame's vertical
        // midline, at the canvas column the transform assigns it.
        const blockText = (id: string, text: string, vCentre: number, fontSize: number, fontWeight: string, fill: string, charSpacing?: number) => {
            parts.push(new fabric.Text(text, {
                id,
                left: boxLeft + boxW - vCentre,
                top: boxTop + centreU,
                fontSize,
                fontWeight,
                fontFamily: 'Arial',
                fill,
                angle: 90,
                originX: 'center',
                originY: 'center',
                ...(charSpacing ? { charSpacing: charSpacing * scale } : {}),
                selectable: true, evented: true, hasControls: true
            } as any));
        };
        // A rect given in virtual coordinates (u0, v0, width along
        // u, height along v), mapped into the tall frame: the
        // dimensions swap, and the canvas left is set by the rect's
        // virtual FAR edge (v0 + h).
        const blockRect = (id: string, u0: number, v0: number, w: number, h: number, rectOpts: any) => new fabric.Rect({
            id, // UNIQUE ID
            left: boxLeft + boxW - (v0 + h),
            top: boxTop + u0,
            width: h,
            height: w,
            ...rectOpts,
            selectable: true, evented: true, hasControls: true
        } as any);

        features.forEach((line, i) => {
            blockText(`security-feature-${i}`, line, featuresTop + (i * pitch) + (pitch / 2), featureFont, '600', '#111');
        });
        blockText('security-header', "SECURITY FEATURES", blockTop + (titleH / 2), titleFont, 'bold', '#111', 2);

        // The Chemical Wash Detection Box — chemical reactivity is
        // a property of the PAPER, and its verification furniture is
        // a deliberately BLANK area a verifier watches for
        // discoloration. It closes the panel's stack at the frame's
        // left (Princewill's seat for it, in the rotated pattern:
        // after the feature list along the reading axis), in the
        // design family's own style, and its field stays genuinely
        // blank in every style. The label never sits inside the
        // blank field: beside it on the list side
        // (plain/double/tinted/corner) or in the header band (header
        // style — the band caps the end of the box nearest the
        // list). All parts are composed in virtual coordinates and
        // mapped by blockRect / the label helper, so the five
        // treatments keep their exact relative geometry.
        if (washStyle) {
            const ink = opts?.frameStroke || '#33475f';
            const washLabel = (vCentre: number, fill: string) => {
                parts.push(new fabric.Text('CHEMICAL WASH DETECTION', {
                    id: 'chem-wash-label', // UNIQUE ID
                    left: boxLeft + boxW - vCentre,
                    top: boxTop + centreU,
                    fontSize: 4.5 * scale,
                    fontFamily: 'Arial',
                    fill,
                    angle: 90,
                    originX: 'center',
                    originY: 'center',
                    selectable: true, evented: true, hasControls: true
                } as any));
            };
            const washRect = (fill: string, stroke: string, strokeWidth: number) =>
                blockRect('chem-wash-box', washLeft, washBoxTop, washW, washH, { fill, stroke, strokeWidth });
            if (washStyle === 'header') {
                parts.push(washRect('#ffffff', ink, 0.8 * scale));
                parts.push(blockRect('chem-wash-band', washLeft, washBoxTop, washW, 11 * scale, { fill: ink }));
                washLabel(washBoxTop + (5.5 * scale), '#ffffff');
            } else if (washStyle === 'double') {
                washLabel(washLabelTop + (washLabelH / 2), '#666666');
                parts.push(washRect('#ffffff', ink, 0.8 * scale));
                parts.push(blockRect('chem-wash-inner', washLeft + (3 * scale), washBoxTop + (3 * scale), washW - (6 * scale), washH - (6 * scale), { fill: 'transparent', stroke: ink, strokeWidth: 0.6 * scale }));
            } else if (washStyle === 'tinted') {
                washLabel(washLabelTop + (washLabelH / 2), ink);
                parts.push(washRect(paleMix(ink) || '#eef2f7', ink, 0.8 * scale));
            } else if (washStyle === 'corner') {
                washLabel(washLabelTop + (washLabelH / 2), '#888888');
                parts.push(washRect('#ffffff', 'rgba(0,0,0,0)', 0));
                const arm = 8 * scale, thick = 0.9 * scale;
                const u0 = washLeft, u1 = washLeft + washW, v0 = washBoxTop, v1 = washBoxTop + washH;
                const arms: Array<[number, number, number, number]> = [
                    [u0, v0, arm, thick], [u0, v0, thick, arm],
                    [u1 - arm, v0, arm, thick], [u1 - thick, v0, thick, arm],
                    [u0, v1 - thick, arm, thick], [u0, v1 - arm, thick, arm],
                    [u1 - arm, v1 - thick, arm, thick], [u1 - thick, v1 - arm, thick, arm]
                ];
                arms.forEach(([u, v, w, h], i) => {
                    parts.push(blockRect(`chem-wash-corner-${i}`, u, v, w, h, { fill: '#999999' }));
                });
            } else {
                // plain — the canonical treatment.
                washLabel(washLabelTop + (washLabelH / 2), '#666666');
                parts.push(washRect('#ffffff', '#999999', 0.8 * scale));
            }
        }

        // The compliance footnote is NOT part of the panel group.
        // On the reference back it runs vertically along the sheet's
        // far-left edge, in the specimen's direction like the panel
        // write-up (image-33.png): its own column at regccColumnX, centred on
        // the frame's span, clear of the frame's left border. It is
        // emitted as its own top-level object so the panel group's
        // bounds stay exactly the frame's — the property that keeps
        // arrangement entries saved before the specimen pattern
        // (frame-anchored left/top) landing the panel in the same
        // seat, with no remnant of the old composition.
        if (footnoteText) {
            objects.push(new fabric.Text(footnoteText, {
                id: 'security-regcc', // UNIQUE ID
                left: G.regccColumnX * scale,
                top: boxTop + (boxH / 2),
                fontSize: 4.5 * scale,
                fontFamily: 'Arial',
                fill: '#333',
                angle: 90,
                originX: 'center',
                originY: 'center',
                selectable: true, evented: true, hasControls: true
            } as any));
        }

        // Emit the panel as ONE unit. The group is constructed from
        // the parts' absolute coordinates and anchored at the frame's
        // top-left, so every part renders exactly where it did as a
        // sibling object (verified: composed transforms are identical
        // grouped vs ungrouped, and a group move shifts every part
        // rigidly). One stable id — the arrangement system records a
        // single transform for the whole panel under 'security-panel'.
        objects.push(new fabric.Group(parts, {
            id: 'security-panel', // UNIQUE ID — the whole panel is one draggable unit
            left: boxLeft, top: boxTop,
            selectable: true, evented: true, hasControls: true
        } as any));
    }

}

/**
 * Mix a #rrggbb colour toward white — the printer's "screen" version of
 * a design's ink. ratio 0.92 ≈ the spec's ~92% pale mix (§5.2): light
 * enough that bank stamps over it stay legible. Returns null for
 * anything that is not a 6-digit hex colour.
 */
export function paleMix(hex: string, ratio = 0.92): string | null {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || '').trim());
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const mix = (c: number) => Math.round(c + (255 - c) * ratio);
    const r = mix((n >> 16) & 255), g = mix((n >> 8) & 255), b = mix(n & 255);
    return `rgb(${r}, ${g}, ${b})`;
}
